import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Plus,
    Pencil,
    Trash2,
    Search,
    X,
    Boxes,
    AlertTriangle,
} from "lucide-react";
import StatusPill from "../../components/primitives/StatusPill";
import TopStatusStrip from "../../components/primitives/TopStatusStrip";
import TextButton from "../../components/primitives/TextButton";
import IconButton from "../../components/primitives/IconButton";
import EmptyState from "../../components/primitives/EmptyState";
import SegmentedControl from "../../components/primitives/SegmentedControl";
import { deleteSku, ApiError } from "../../services/api";
import { useSkus, loadSkus, invalidateSkus } from "../../services/skuStore";
import SkuFormModal from "./SkuFormModal";
import "../../styles/viz-shell.css";
import "./CatalogPage.css";

function fmtDim(n) {
    if (n == null) return "—";
    return Number(n) % 1 === 0 ? String(Number(n)) : Number(n).toFixed(2);
}

function fmtVolume(w, h, d) {
    const v = Number(w) * Number(h) * Number(d);
    if (!Number.isFinite(v)) return "—";
    return v.toFixed(0);
}

function fmtUpdated(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    const now = Date.now();
    const diffSec = Math.floor((now - d.getTime()) / 1000);
    if (diffSec < 60) return "just now";
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}d ago`;
    return d.toLocaleDateString();
}

const SORT_META = {
    name: (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    "name-desc": (a, b) =>
        b.name.localeCompare(a.name, undefined, { sensitivity: "base" }),
    updated: (a, b) =>
        new Date(b.updated_at || 0) - new Date(a.updated_at || 0),
};

function CatalogPage() {
    const { skus, loading, error, refresh } = useSkus();
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState("updated");
    const [modal, setModal] = useState(null); // { mode: "create" | "edit", sku? }
    const [confirmId, setConfirmId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [opError, setOpError] = useState(null);
    const confirmTimerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
        };
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        let list = skus;
        if (q) {
            list = list.filter((s) => {
                const n = (s.name ?? "").toLowerCase();
                const c = (s.sku_code ?? "").toLowerCase();
                return n.includes(q) || c.includes(q);
            });
        }
        return [...list].sort(SORT_META[sort] || SORT_META.updated);
    }, [skus, search, sort]);

    const status = error ? "error" : loading ? "loading" : "ready";
    const statusMeta = {
        ready: { label: "Ready", tone: "success" },
        loading: { label: "Loading", tone: "working", pulse: true },
        error: { label: "Error", tone: "danger" },
    }[status];

    const askDelete = (id) => {
        if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
        setConfirmId(id);
        confirmTimerRef.current = setTimeout(() => setConfirmId(null), 4000);
    };

    const cancelDelete = () => {
        if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
        setConfirmId(null);
    };

    const doDelete = async (id) => {
        cancelDelete();
        setDeletingId(id);
        setOpError(null);
        try {
            await deleteSku(id);
            invalidateSkus();
            await loadSkus({ force: true });
        } catch (err) {
            const msg =
                err instanceof ApiError
                    ? err.message
                    : err?.message || "Delete failed";
            setOpError(msg);
        } finally {
            setDeletingId(null);
        }
    };

    const closeModal = () => setModal(null);
    const onSaved = () => closeModal();

    const isEmpty = !loading && skus.length === 0 && !error;
    const showFiltered = !loading && skus.length > 0;
    const noMatch = showFiltered && filtered.length === 0;

    return (
        <div className="viz-page">
            <TopStatusStrip
                left={
                    <>
                        <span className="uppercase-label">Catalog</span>
                        <span className="mono-num catalog-count">
                            {skus.length} {skus.length === 1 ? "SKU" : "SKUs"}
                        </span>
                    </>
                }
                center={
                    <StatusPill tone={statusMeta.tone} pulse={statusMeta.pulse}>
                        {statusMeta.label}
                    </StatusPill>
                }
                right={
                    error ? (
                        <button
                            type="button"
                            className="catalog-retry mono"
                            onClick={() => refresh().catch(() => {})}
                        >
                            Retry
                        </button>
                    ) : null
                }
            />

            <div className="viz-page__body catalog-body">
                <main className="catalog-main">
                    <header className="catalog-toolbar">
                        <div className="catalog-search">
                            <Search size={14} className="catalog-search__icon" aria-hidden="true" />
                            <input
                                type="search"
                                className="catalog-search__input focus-ring"
                                placeholder="Search by name or SKU code"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                aria-label="Search catalog"
                            />
                            {search && (
                                <button
                                    type="button"
                                    className="catalog-search__clear"
                                    onClick={() => setSearch("")}
                                    aria-label="Clear search"
                                >
                                    <X size={12} aria-hidden="true" />
                                </button>
                            )}
                        </div>

                        <div className="catalog-toolbar__sort">
                            <SegmentedControl
                                ariaLabel="Sort order"
                                value={sort}
                                onChange={setSort}
                                options={[
                                    { value: "updated", label: "Updated" },
                                    { value: "name", label: "Name A-Z" },
                                ]}
                            />
                        </div>

                        <TextButton
                            variant="primary"
                            icon={Plus}
                            onClick={() => setModal({ mode: "create" })}
                        >
                            New SKU
                        </TextButton>
                    </header>

                    {opError && (
                        <div className="viz-error catalog-banner" role="alert">
                            <AlertTriangle size={14} aria-hidden="true" />
                            <span>{opError}</span>
                        </div>
                    )}

                    {error && skus.length === 0 && (
                        <div className="viz-error catalog-banner" role="alert">
                            <AlertTriangle size={14} aria-hidden="true" />
                            <span>{error.message || "Failed to load catalog"}</span>
                        </div>
                    )}

                    {isEmpty ? (
                        <EmptyState
                            icon={Boxes}
                            eyebrow="Catalog · empty"
                            title="No SKUs yet"
                            lede="Saved SKUs let you fill manifest rows in one click instead of typing dimensions every time."
                            action={
                                <TextButton
                                    variant="primary"
                                    icon={Plus}
                                    onClick={() => setModal({ mode: "create" })}
                                >
                                    Add your first SKU
                                </TextButton>
                            }
                        />
                    ) : showFiltered ? (
                        <div className="catalog-table" role="table">
                            <div className="catalog-table__head" role="row">
                                <span className="catalog-cell catalog-cell--name uppercase-label">Name</span>
                                <span className="catalog-cell catalog-cell--code uppercase-label">SKU</span>
                                <span className="catalog-cell catalog-cell--dims uppercase-label">W × H × D</span>
                                <span className="catalog-cell catalog-cell--vol uppercase-label">Volume</span>
                                <span className="catalog-cell catalog-cell--weight uppercase-label">Weight</span>
                                <span className="catalog-cell catalog-cell--updated uppercase-label">Updated</span>
                                <span className="catalog-cell catalog-cell--actions" />
                            </div>

                            {noMatch ? (
                                <div className="catalog-no-match">
                                    <span className="uppercase-label">No matches for</span>
                                    <span className="mono">"{search}"</span>
                                    <button
                                        type="button"
                                        className="catalog-no-match__clear mono"
                                        onClick={() => setSearch("")}
                                    >
                                        Clear
                                    </button>
                                </div>
                            ) : (
                                filtered.map((sku) => {
                                    const confirming = confirmId === sku.id;
                                    const deleting = deletingId === sku.id;
                                    return (
                                        <div
                                            key={sku.id}
                                            className={`catalog-row ${deleting ? "catalog-row--deleting" : ""}`}
                                            role="row"
                                        >
                                            <span className="catalog-cell catalog-cell--name">
                                                <span className="catalog-name" title={sku.name}>
                                                    {sku.name}
                                                </span>
                                                {sku.description && (
                                                    <span
                                                        className="catalog-desc"
                                                        title={sku.description}
                                                    >
                                                        {sku.description}
                                                    </span>
                                                )}
                                            </span>
                                            <span className="catalog-cell catalog-cell--code mono">
                                                {sku.sku_code || (
                                                    <span className="catalog-empty">—</span>
                                                )}
                                            </span>
                                            <span className="catalog-cell catalog-cell--dims mono-num">
                                                {fmtDim(sku.width)} × {fmtDim(sku.height)} × {fmtDim(sku.depth)}
                                            </span>
                                            <span className="catalog-cell catalog-cell--vol mono-num">
                                                {fmtVolume(sku.width, sku.height, sku.depth)}
                                            </span>
                                            <span className="catalog-cell catalog-cell--weight mono-num">
                                                {sku.weight != null ? (
                                                    <>
                                                        {fmtDim(sku.weight)}
                                                        <span className="catalog-unit">lb</span>
                                                    </>
                                                ) : (
                                                    <span className="catalog-empty">—</span>
                                                )}
                                            </span>
                                            <span className="catalog-cell catalog-cell--updated mono">
                                                {fmtUpdated(sku.updated_at)}
                                            </span>
                                            <span className="catalog-cell catalog-cell--actions">
                                                {confirming ? (
                                                    <div className="catalog-confirm">
                                                        <button
                                                            type="button"
                                                            className="catalog-confirm__yes mono"
                                                            onClick={() => doDelete(sku.id)}
                                                        >
                                                            Confirm delete
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="catalog-confirm__no mono"
                                                            onClick={cancelDelete}
                                                            aria-label="Cancel delete"
                                                        >
                                                            <X size={12} aria-hidden="true" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="catalog-row__actions">
                                                        <IconButton
                                                            icon={Pencil}
                                                            label={`Edit ${sku.name}`}
                                                            size="sm"
                                                            onClick={() =>
                                                                setModal({ mode: "edit", sku })
                                                            }
                                                            disabled={deleting}
                                                        />
                                                        <IconButton
                                                            icon={Trash2}
                                                            label={`Delete ${sku.name}`}
                                                            size="sm"
                                                            tone="danger"
                                                            onClick={() => askDelete(sku.id)}
                                                            disabled={deleting}
                                                        />
                                                    </div>
                                                )}
                                            </span>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    ) : loading ? (
                        <div className="catalog-loading mono uppercase-label">
                            Loading catalog…
                        </div>
                    ) : null}
                </main>
            </div>

            <SkuFormModal
                open={!!modal}
                mode={modal?.mode || "create"}
                initial={modal?.sku}
                onClose={closeModal}
                onSaved={onSaved}
            />
        </div>
    );
}

export default CatalogPage;
